// Shared scaffolding for all promo routes.
// 1080x1920 vertical, 15-second auto-played montage for IG Reels/Stories.
//
// IG safe margins (worst-case device with home indicator + caption + actions):
//   top: 360px (handle + UI)
//   bottom: 580px (caption + send-message row + action column + audio mark)
// Full-bleed slides should use pb-[620px] for their content overlay.

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export const SAFE_TOP = 360;
export const SAFE_BOTTOM = 580;
export const FULL_PB = 620;

export const PHOTOS = {
  hero: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/5mpzbdp8_sweat_with_slay_7.png",
  about: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/nb2spi5w_IMG_8014%20%281%29.jpg",
  social: "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/c7xf6eol_CEBC811F-CCAB-4A09-A66E-6D8B9203D7F0.jpeg",
};

export const BgPhoto = ({ src, kenBurns = "in" }) => (
  <motion.img
    src={src}
    alt=""
    initial={{ scale: kenBurns === "in" ? 1.0 : 1.12, opacity: 0 }}
    animate={{ scale: kenBurns === "in" ? 1.12 : 1.0, opacity: 1 }}
    transition={{ duration: 3.0, ease: "linear" }}
    className="absolute inset-0 w-full h-full object-cover"
  />
);

export function PromoStage({ slides }) {
  const TOTAL = slides.reduce((acc, s) => acc + s.duration, 0);
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const imgs = Array.from(document.images);
    if (imgs.length === 0) { setReady(true); return; }
    const pending = imgs.filter((i) => !(i.complete && i.naturalHeight > 0));
    if (pending.length === 0) { setReady(true); return; }
    let left = pending.length;
    const done = () => { left -= 1; if (left <= 0) setReady(true); };
    pending.forEach((i) => { i.addEventListener("load", done, { once: true }); i.addEventListener("error", done, { once: true }); });
    const fallback = setTimeout(() => setReady(true), 8000);
    return () => clearTimeout(fallback);
  }, []);

  useEffect(() => {
    if (!ready) return;
    document.body.setAttribute("data-promo-started", "true");
    const start = Date.now();
    let raf;
    const tick = () => {
      const elapsed = Date.now() - start;
      let total = 0;
      let active = 0;
      for (let i = 0; i < slides.length; i++) {
        total += slides[i].duration;
        if (elapsed < total) { active = i; break; }
        active = i;
      }
      setIdx(active);
      setProgress(Math.min(1, elapsed / TOTAL));
      if (elapsed < TOTAL) raf = requestAnimationFrame(tick);
      else document.body.setAttribute("data-promo-done", "true");
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const slide = slides[idx];
  return (
    <main
      data-testid="promo-page"
      data-promo-active={slide.id}
      className="font-body overflow-hidden fixed inset-0"
      style={{ background: slide.bg, color: "#fff" }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay z-20"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }}
      />
      <AnimatePresence>
        <motion.div
          key={slide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className={slide.full ? "absolute inset-0" : "absolute left-0 right-0 px-16"}
          style={slide.full ? {} : { top: SAFE_TOP, bottom: SAFE_BOTTOM }}
        >
          {slide.render()}
        </motion.div>
      </AnimatePresence>
      <div className="absolute left-12 right-12 h-1 bg-white/15 z-30" style={{ top: SAFE_TOP - 30 }}>
        <div className="h-full bg-white" style={{ width: `${progress * 100}%`, transition: "width 80ms linear" }} />
      </div>
      <div
        className="absolute left-0 right-0 text-center text-white/75 text-base uppercase tracking-[0.4em] font-semibold z-30"
        style={{ bottom: SAFE_BOTTOM - 80 }}
      >
        sweatwithslay.com
      </div>
    </main>
  );
}
